/**
 * AlertQualityPanel.tsx — MER Algo Footprints v7.1B — Phase 7.1B
 *
 * Presentation-only alert quality layer.
 * Reads from alertStore + journal71aEntries.
 * Never modifies alert generation, alertEngine, or engine logic.
 *
 * Per-alert:
 *   - Category label + severity icon
 *   - DedupeKey display (shows cooldown identity)
 *   - Attribution context: structure/liquidity/regime/gamma derived from
 *     alert category+message via a static lookup — no engine calls
 *   - Confidence stability indicator derived from journal score history
 *     for the alert's symbol (entirely read-only)
 *
 * UI-only filters: never suppress alerts from the store.
 * Breakdown tab: severity counts, category bars, cooldown windows.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  getAlerts, markRead, markAllRead, clearAlerts,
  subscribeAlerts, Alert, AlertCategory, AlertSeverity,
} from '../alerts/alertStore';
import { useStore } from '../../store';
import { getSymbolContext } from '../../selectors/symbolContext';

// ─── Severity config ──────────────────────────────────────────────────────────

const SEV_CFG: Record<AlertSeverity, { color: string; bg: string; icon: string; label: string }> = {
  info:         { color: 'var(--blue-muted)', bg: 'var(--surface2)', icon: '○', label: 'Info'         },
  warning:      { color: 'var(--amber)',      bg: 'var(--amber)12',  icon: '⚠', label: 'Warning'      },
  actionable:   { color: 'var(--green)',      bg: 'var(--green)12',  icon: '⚡', label: 'Actionable'   },
  invalidation: { color: 'var(--red)',        bg: 'var(--red)12',    icon: '✕', label: 'Invalidation' },
};

// ─── Category config ──────────────────────────────────────────────────────────

const CAT_CFG: Record<AlertCategory, { color: string; label: string }> = {
  structure:      { color: 'var(--purple)', label: 'Structure'      },
  sweep:          { color: 'var(--cyan)',   label: 'Sweep'          },
  liquidity_zone: { color: 'var(--blue)',   label: 'Liquidity Zone' },
  vwap:           { color: 'var(--gold)',   label: 'VWAP'           },
  setup:          { color: 'var(--green)',  label: 'Setup'          },
  thesis:         { color: 'var(--purple)', label: 'Thesis'         },
  opportunity:    { color: 'var(--green)',  label: 'Opportunity'    },
  feed:           { color: 'var(--t3)',     label: 'Feed'           },
  ai:             { color: 'var(--purple)', label: 'AI'             },
};

const COOLDOWN_MS: Record<AlertSeverity, number> = {
  info: 60_000, warning: 30_000, actionable: 15_000, invalidation: 10_000,
};

const COOLDOWN_LABEL: Record<AlertSeverity, string> = {
  info: '60s', warning: '30s', actionable: '15s', invalidation: '10s',
};

// ─── Live attribution context from OpportunityScore ──────────────────────────

interface AttribContext {
  structure: string | null;
  liquidity: string | null;
  regime:    string | null;
  gamma:     string | null;
  isLive:    boolean;
}

// Derives structure/liquidity/regime/gamma context from the live score for the
// alert's symbol — the same data AttributionPanel sections A–E already display.
// Read-only: calls getSymbolContext (memoized, no side effects).
// Falls back to static category/title lookup when no live score is available.

function useAlertAttribContext(alert: Alert): AttribContext {
  const { symbolData, settings, weights } = useStore(s => ({
    symbolData: s.symbolData,
    settings:   s.settings,
    weights:    s.weights,
  }));

  return useMemo((): AttribContext => {
    const cat   = alert.category;
    const title = alert.title.toLowerCase();
    const msg   = alert.message.toLowerCase();

    // ── Try to get live OpportunityScore data for this symbol ────────────────
    let structure: string | null = null;
    let liquidity: string | null = null;
    let regime:    string | null = null;
    let gamma:     string | null = null;

    if (alert.symbol) {
      try {
        const ctx = getSymbolContext(alert.symbol, symbolData[alert.symbol], settings, weights);
        if (ctx) {
          // Structure — from SMC bias + score components
          const smcBias = ctx.smc?.bias ?? null;
          const biasPct = ctx.smc?.biasStrength ?? null;
          if (smcBias && smcBias !== 'neutral') {
            structure = `SMC bias ${smcBias} (${biasPct ?? 'minor'}) — ${
              cat === 'structure' ? alert.title :
              smcBias === 'bullish' ? 'structure supports long setups' : 'structure supports short setups'
            }`;
          } else if (cat === 'structure' || cat === 'setup') {
            structure = alert.title + (alert.message ? ` — ${alert.message}` : '');
          }

          // Liquidity — from zones + trap state
          const nearZones = ctx.zones?.length ?? 0;
          const trapCount = ctx.traps?.length ?? 0;
          if (cat === 'sweep') {
            liquidity = title.includes('reclaim')
              ? 'Sweep + reclaim: stop hunt resolved — potential institutional reversal entry'
              : 'Liquidity swept: pool taken out — watch for price to reclaim key level';
          } else if (cat === 'liquidity_zone') {
            liquidity = `At key liquidity zone — ${nearZones} zone${nearZones !== 1 ? 's' : ''} tracked${
              trapCount > 0 ? `, ${trapCount} trap signal${trapCount !== 1 ? 's' : ''} active` : ''
            }`;
          } else if (cat === 'vwap') {
            liquidity = title.includes('reclaim')
              ? 'VWAP reclaimed — institutional fair value support restored'
              : 'VWAP lost — price below institutional midpoint, bearish pressure';
          } else if (nearZones > 0) {
            liquidity = `${nearZones} liquidity zone${nearZones !== 1 ? 's' : ''} tracked for this symbol`;
          }

          // Regime — from live regime label + posture
          const regimeLabel   = ctx.regime?.label   ?? null;
          const regimePosture = ctx.regime?.posture  ?? null;
          const regimeConf    = ctx.regime?.confidence ?? null;
          if (regimeLabel) {
            regime = `${regimeLabel}${regimePosture ? ` — ${regimePosture}` : ''}${
              regimeConf !== null ? ` (${regimeConf}% confidence)` : ''
            }`;
          } else if (cat === 'thesis') {
            regime = title.includes('shift') ? 'Regime shift detected — reassess directional bias'
              : title.includes('invalidation') ? 'Thesis invalidation risk — setup assumptions challenged'
              : title.includes('trap') ? 'Trap condition — regime-driven false move risk'
              : 'Thesis / regime update';
          }

          // Gamma — from live gamma result
          const gammaResult = ctx.gamma;
          if (gammaResult) {
            gamma = `Gamma ${gammaResult.regime} (${gammaResult.confidence}) — vol risk: ${gammaResult.volRisk}${
              gammaResult.gammaFlip !== null ? `, flip: ${gammaResult.gammaFlip}` : ''
            } — flags off, not applied to scoring`;
          } else {
            gamma = 'Gamma: not available for this symbol (no options data)';
          }

          return { structure, liquidity, regime, gamma, isLive: true };
        }
      } catch {
        // Fall through to static lookup
      }
    }

    // ── Static fallback when no live score available ──────────────────────────
    if (cat === 'structure') {
      if (title.includes('bos'))             structure = 'Break of structure confirmed — directional bias established';
      else if (title.includes('choch'))      structure = 'Change of character — potential reversal in progress';
      else if (title.includes('or break'))   structure = 'Opening range broken — momentum directional signal';
      else if (title.includes('displacement')) structure = 'Displacement candle — impulsive institutional move';
      else if (title.includes('fvg'))        structure = 'Fair value gap filled — equilibrium test';
      else if (title.includes('ob'))         structure = 'Order block tested — institutional level reaction';
      else                                   structure = 'Structural event detected';
    } else if (cat === 'setup') {
      structure = 'High-conviction setup — structure supporting directional bias';
    }
    if (cat === 'sweep') {
      liquidity = title.includes('reclaim')
        ? 'Sweep + reclaim confirmed — stop hunt resolved, potential reversal'
        : 'Liquidity swept — pool taken out, watch for reversal signal';
    } else if (cat === 'liquidity_zone') {
      liquidity = 'Price at key liquidity zone — institutional interest area';
    } else if (cat === 'vwap') {
      liquidity = title.includes('reclaim')
        ? 'VWAP reclaimed — institutional midpoint support restored'
        : 'VWAP lost — price trading below institutional fair value';
    }
    if (cat === 'thesis') {
      regime = title.includes('shift')           ? 'Regime change detected — reassess directional bias'
        : title.includes('confidence')           ? 'Regime confidence changed — signal quality update'
        : title.includes('invalidation')         ? 'Thesis invalidation risk — current setup assumptions challenged'
        : title.includes('trap')                 ? 'Trap condition active — regime-driven false move risk'
        :                                          'Thesis / regime update';
    } else if (msg.includes('regime') || msg.includes('posture')) {
      regime = 'Regime-relevant event — check current posture alignment';
    }
    if (cat === 'opportunity' || cat === 'setup') {
      gamma = 'Gamma status: not applied (flags off — baseline mode)';
    } else if (msg.includes('gamma')) {
      gamma = 'Gamma influence present — see attribution panel for detail';
    }

    return { structure, liquidity, regime, gamma, isLive: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, alert.symbol, symbolData, settings, weights]);
}

// ─── Confidence stability from journal history ────────────────────────────────
// Read-only derivation over journal71aEntries for the alert's symbol.
// Computes score variance to indicate whether operator-observed confidence
// for this symbol has been stable or volatile.

type StabilityLabel = 'stable' | 'volatile' | 'insufficient';

interface StabilityResult {
  label:   StabilityLabel;
  color:   string;
  detail:  string;
  count:   number;
}

function deriveStability(
  symbol: string,
  entries: Array<{ symbol: string; score: number | null; ts: number }>,
): StabilityResult {
  const scores = entries
    .filter(e => e.symbol === symbol && e.score !== null)
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 8)
    .map(e => e.score as number);

  if (scores.length < 3) {
    return { label: 'insufficient', color: 'var(--t3)', detail: `${scores.length} score observations`, count: scores.length };
  }

  const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
  const variance = scores.reduce((a, b) => a + (b - mean) ** 2, 0) / scores.length;
  const stdDev = Math.sqrt(variance);

  if (stdDev <= 8) {
    return { label: 'stable',   color: 'var(--green)', detail: `±${stdDev.toFixed(1)} std dev over ${scores.length} obs`, count: scores.length };
  } else {
    return { label: 'volatile', color: 'var(--amber)', detail: `±${stdDev.toFixed(1)} std dev over ${scores.length} obs`, count: scores.length };
  }
}

// ─── Cooldown countdown display ───────────────────────────────────────────────

function CooldownBadge({ alert }: { alert: Alert }) {
  const elapsed = Date.now() - alert.timestamp;
  const window  = COOLDOWN_MS[alert.severity];
  const remaining = Math.max(0, window - elapsed);
  const active  = remaining > 0;
  const remSec  = Math.ceil(remaining / 1000);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
      <span style={{
        fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
        color: active ? 'var(--amber)' : 'var(--t3)',
        border: `1px solid ${active ? 'var(--amber)44' : 'var(--border)'}`,
        background: active ? 'var(--amber)08' : 'transparent',
        padding: '0 4px', borderRadius: 2,
      }}>
        {active ? `COOLDOWN ${remSec}s` : 'READY'}
      </span>
      <span style={{ fontSize: 7, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>
        window: {COOLDOWN_LABEL[alert.severity]}
      </span>
    </div>
  );
}

// ─── DedupeKey display ────────────────────────────────────────────────────────

function DedupeKeyBadge({ dedupeKey }: { dedupeKey: string }) {
  return (
    <div style={{
      fontSize: 7, fontFamily: 'var(--mono)', color: 'var(--blue-dim)',
      marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }} title={`Dedupe key: ${dedupeKey}`}>
      key: {dedupeKey}
    </div>
  );
}

// ─── Attribution context strip ────────────────────────────────────────────────

function AttribStrip({ ctx, isLive }: { ctx: AttribContext; isLive: boolean }) {
  const rows = [
    { l: 'Structure', v: ctx.structure, c: 'var(--purple)' },
    { l: 'Liquidity', v: ctx.liquidity, c: 'var(--cyan)'   },
    { l: 'Regime',    v: ctx.regime,    c: 'var(--gold)'   },
    { l: 'Gamma',     v: ctx.gamma,     c: 'var(--green)'  },
  ].filter(r => r.v !== null);

  if (!rows.length) return null;

  return (
    <div style={{
      marginTop: 5, padding: '5px 7px',
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 3,
    }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 3 }}>
        <span style={{
          fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
          color: isLive ? 'var(--green)' : 'var(--blue-dim)',
          border: `1px solid ${isLive ? 'var(--green)44' : 'var(--border)'}`,
          background: isLive ? 'var(--green)10' : 'transparent',
          padding: '0 4px', borderRadius: 2,
        }}>{isLive ? 'LIVE CONTEXT' : 'STATIC LOOKUP'}</span>
      </div>
      {rows.map(r => (
        <div key={r.l} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{
            fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
            color: r.c, minWidth: 52, flexShrink: 0,
          }}>{r.l}</span>
          <span style={{ fontSize: 8, color: 'var(--blue-muted)', lineHeight: 1.4 }}>{r.v}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Full quality alert row ───────────────────────────────────────────────────

function QualityAlertRow({ alert, stability, onRead }: {
  alert: Alert;
  stability: StabilityResult | null;
  onRead: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEV_CFG[alert.severity];
  const cat = CAT_CFG[alert.category];
  // Uses live OpportunityScore data when available; falls back to static lookup
  const ctx = useAlertAttribContext(alert);

  const hasDetail = ctx.structure || ctx.liquidity || ctx.regime || ctx.gamma;

  return (
    <div style={{
      padding: '7px 10px',
      background: alert.read ? 'transparent' : sev.bg,
      borderLeft: `3px solid ${alert.read ? 'var(--border)' : sev.color}`,
      borderRadius: '0 4px 4px 0', marginBottom: 3,
      transition: 'background .12s', opacity: alert.read ? 0.6 : 1,
    }}>
      {/* Title row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', cursor: 'pointer' }}
        onClick={() => { onRead(); setExpanded(p => !p); }}>
        <span style={{ fontSize: 11, color: sev.color, flexShrink: 0 }}>{sev.icon}</span>
        {alert.symbol && (
          <span style={{ fontSize: 10, fontWeight: 700, color: sev.color, fontFamily: 'var(--mono)' }}>
            {alert.symbol}
          </span>
        )}
        <span style={{ fontSize: 10, fontWeight: 700, color: alert.read ? 'var(--blue-dim)' : 'var(--text)', flex: 1 }}>
          {alert.title}
        </span>
        {/* Category label */}
        <span style={{
          fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
          color: cat.color, border: `1px solid ${cat.color}44`,
          background: `${cat.color}10`, padding: '0 4px', borderRadius: 2, flexShrink: 0,
        }}>{cat.label.toUpperCase()}</span>
        {/* Confidence stability */}
        {stability && stability.label !== 'insufficient' && (
          <span style={{
            fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
            color: stability.color, border: `1px solid ${stability.color}44`,
            background: `${stability.color}10`, padding: '0 4px', borderRadius: 2, flexShrink: 0,
          }}>{stability.label.toUpperCase()}</span>
        )}
        {/* Age + expand arrow */}
        <span style={{ fontSize: 8, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', flexShrink: 0 }}>
          {Math.floor((Date.now() - alert.timestamp) / 1000) < 60
            ? `${Math.floor((Date.now() - alert.timestamp) / 1000)}s`
            : `${Math.floor((Date.now() - alert.timestamp) / 60000)}m`}
        </span>
        {(hasDetail || stability) && (
          <span style={{ fontSize: 8, color: 'var(--blue-dim)', flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
        )}
      </div>

      {/* Message */}
      <div style={{ fontSize: 9, color: 'var(--blue-muted)', lineHeight: 1.4, marginTop: 2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap' }}>
        {alert.message}
      </div>

      {/* DedupeKey + cooldown — always visible */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <DedupeKeyBadge dedupeKey={alert.dedupeKey} />
        <CooldownBadge alert={alert} />
      </div>

      {/* Expanded: attribution context + stability detail */}
      {expanded && (
        <>
          {hasDetail && <AttribStrip ctx={ctx} isLive={ctx.isLive} />}
          {stability && stability.label !== 'insufficient' && (
            <div style={{
              marginTop: 5, fontSize: 8, color: stability.color,
              fontFamily: 'var(--mono)',
            }}>
              Score stability ({alert.symbol}): {stability.label} — {stability.detail}
            </div>
          )}
          {stability && stability.label === 'insufficient' && (
            <div style={{ marginTop: 5, fontSize: 8, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>
              Score stability: insufficient data ({stability.count} observation{stability.count !== 1 ? 's' : ''})
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Category bar ─────────────────────────────────────────────────────────────

function CategoryBar({ category, count, total }: { category: AlertCategory; count: number; total: number }) {
  const cfg = CAT_CFG[category];
  const pct = total > 0 ? count / total * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 9, color: cfg.color, minWidth: 90, fontFamily: 'var(--mono)' }}>{cfg.label}</span>
      <div style={{ flex: 1, height: 5, background: 'var(--surface)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 2, transition: 'width .2s' }} />
      </div>
      <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: cfg.color, minWidth: 22, textAlign: 'right' }}>{count}</span>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

type QView = 'stream' | 'breakdown';

export function AlertQualityPanel() {
  const [, rerender]  = useState(0);
  const [qview, setQview]       = useState<QView>('stream');
  const [sevFilter, setSevFilter] = useState<AlertSeverity | 'all'>('all');
  const [catFilter, setCatFilter] = useState<AlertCategory | 'all'>('all');

  const entries = useStore(s => s.journal71aEntries);

  useEffect(() => {
    const unsub = subscribeAlerts(() => rerender(n => n + 1));
    const tick  = setInterval(() => rerender(n => n + 1), 10_000);
    return () => { unsub(); clearInterval(tick); };
  }, []);

  const allAlerts = getAlerts({ limit: 200 });

  // UI-only filter — alertStore is never modified
  const filtered = allAlerts.filter(a => {
    if (sevFilter !== 'all' && a.severity !== sevFilter) return false;
    if (catFilter !== 'all' && a.category !== catFilter) return false;
    return true;
  });

  // Pre-compute stability for every unique symbol in view
  const stabilityMap = useMemo(() => {
    const syms = [...new Set(allAlerts.map(a => a.symbol).filter(Boolean))] as string[];
    const m = new Map<string, StabilityResult>();
    for (const sym of syms) m.set(sym, deriveStability(sym, entries));
    return m;
  }, [allAlerts, entries]);

  // Category + severity aggregates
  const catCounts = new Map<AlertCategory, number>();
  for (const a of allAlerts) catCounts.set(a.category, (catCounts.get(a.category) ?? 0) + 1);
  const sevCounts: Record<AlertSeverity, number> = { actionable: 0, warning: 0, invalidation: 0, info: 0 };
  for (const a of allAlerts) sevCounts[a.severity]++;

  const tabBtn = (active: boolean): React.CSSProperties => ({
    fontSize: 9, fontFamily: 'var(--mono)', fontWeight: active ? 700 : 400,
    color: active ? 'var(--gold)' : 'var(--t3)',
    background: active ? 'var(--gold)12' : 'none',
    border: `1px solid ${active ? 'var(--gold)55' : 'var(--border)'}`,
    padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
  });

  const filterBtn = (active: boolean, color: string): React.CSSProperties => ({
    fontSize: 8, fontFamily: 'var(--mono)', fontWeight: active ? 700 : 400,
    color: active ? color : 'var(--t3)',
    border: `1px solid ${active ? color + '55' : 'var(--border)'}`,
    background: active ? color + '12' : 'none',
    padding: '2px 6px', borderRadius: 3, cursor: 'pointer',
  });

  return (
    <div>
      {/* Top tabs */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 10, alignItems: 'center' }}>
        <button style={tabBtn(qview === 'stream')}    onClick={() => setQview('stream')}>
          Stream ({filtered.length})
        </button>
        <button style={tabBtn(qview === 'breakdown')} onClick={() => setQview('breakdown')}>
          Breakdown
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button onClick={() => markAllRead()}
            style={{ fontSize: 8, color: 'var(--blue-dim)', background: 'none',
              border: '1px solid var(--border)', padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>
            Read all
          </button>
          <button onClick={() => clearAlerts()}
            style={{ fontSize: 8, color: 'var(--red)', background: 'none',
              border: '1px solid var(--red)44', padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      </div>

      {/* ─── Stream tab ─────────────────────────────── */}
      {qview === 'stream' && (
        <>
          {/* Severity filter */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
            <button style={filterBtn(sevFilter === 'all', 'var(--gold)')} onClick={() => setSevFilter('all')}>All</button>
            {(['actionable','warning','invalidation','info'] as AlertSeverity[]).map(s => (
              <button key={s} style={filterBtn(sevFilter === s, SEV_CFG[s].color)} onClick={() => setSevFilter(s)}>
                {SEV_CFG[s].icon} {SEV_CFG[s].label}
              </button>
            ))}
          </div>
          {/* Category filter */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            <button style={filterBtn(catFilter === 'all', 'var(--blue-dim)')} onClick={() => setCatFilter('all')}>
              All categories
            </button>
            {(Object.keys(CAT_CFG) as AlertCategory[]).map(c => (
              <button key={c} style={filterBtn(catFilter === c, CAT_CFG[c].color)} onClick={() => setCatFilter(c)}>
                {CAT_CFG[c].label}
              </button>
            ))}
          </div>
          {(sevFilter !== 'all' || catFilter !== 'all') && (
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 6, fontStyle: 'italic' }}>
              UI filter only — underlying alert store is unaffected.
            </div>
          )}

          {/* Alert list */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ fontSize: 10, color: 'var(--t3)', padding: '16px 0', textAlign: 'center' }}>
                No alerts match the current filter.
              </div>
            ) : (
              filtered.map(a => (
                <QualityAlertRow
                  key={a.id}
                  alert={a}
                  stability={a.symbol ? (stabilityMap.get(a.symbol) ?? null) : null}
                  onRead={() => markRead(a.id)}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* ─── Breakdown tab ──────────────────────────── */}
      {qview === 'breakdown' && (
        <>
          {allAlerts.length === 0 ? (
            <div style={{ fontSize: 10, color: 'var(--t3)', padding: '16px 0', textAlign: 'center' }}>
              No alerts yet.
            </div>
          ) : (
            <>
              {/* Severity tiles */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
                  textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>By Severity</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['actionable','warning','invalidation','info'] as AlertSeverity[]).map(s => (
                    <div key={s} style={{
                      flex: 1, background: 'var(--surface2)', border: `1px solid ${SEV_CFG[s].color}44`,
                      borderRadius: 4, padding: '7px 6px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: SEV_CFG[s].color, fontFamily: 'var(--mono)' }}>
                        {sevCounts[s]}
                      </div>
                      <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 2 }}>{SEV_CFG[s].label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category bars */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
                  textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>By Category</div>
                {(Object.keys(CAT_CFG) as AlertCategory[])
                  .filter(c => (catCounts.get(c) ?? 0) > 0)
                  .sort((a, b) => (catCounts.get(b) ?? 0) - (catCounts.get(a) ?? 0))
                  .map(c => <CategoryBar key={c} category={c} count={catCounts.get(c) ?? 0} total={allAlerts.length} />)
                }
              </div>

              {/* Cooldown windows */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
                  textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>Deduplication Windows</div>
                {(['actionable','warning','invalidation','info'] as AlertSeverity[]).map(s => (
                  <div key={s} style={{ display: 'flex', gap: 8, padding: '3px 0',
                    borderBottom: '1px solid var(--surface)', fontSize: 9, alignItems: 'baseline' }}>
                    <span style={{ color: SEV_CFG[s].color, fontFamily: 'var(--mono)', fontWeight: 700, minWidth: 90 }}>
                      {SEV_CFG[s].label}
                    </span>
                    <span style={{ color: 'var(--blue-dim)', fontFamily: 'var(--mono)', minWidth: 30 }}>
                      {COOLDOWN_LABEL[s]}
                    </span>
                    <span style={{ color: 'var(--t3)', fontSize: 8 }}>
                      — same dedupeKey suppressed within window
                    </span>
                  </div>
                ))}
              </div>

              {/* Confidence stability summary */}
              {stabilityMap.size > 0 && (
                <div>
                  <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
                    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>
                    Score Stability (from journal history)
                  </div>
                  {[...stabilityMap.entries()]
                    .filter(([, s]) => s.label !== 'insufficient')
                    .map(([sym, s]) => (
                      <div key={sym} style={{ display: 'flex', gap: 8, padding: '3px 0',
                        borderBottom: '1px solid var(--surface)', fontSize: 9, alignItems: 'baseline' }}>
                        <span style={{ color: 'var(--gold)', fontFamily: 'var(--mono)', fontWeight: 700, minWidth: 50 }}>
                          {sym}
                        </span>
                        <span style={{ color: s.color, fontFamily: 'var(--mono)', fontWeight: 700, minWidth: 70 }}>
                          {s.label.toUpperCase()}
                        </span>
                        <span style={{ color: 'var(--t3)', fontSize: 8 }}>{s.detail}</span>
                      </div>
                    ))
                  }
                  {[...stabilityMap.values()].every(s => s.label === 'insufficient') && (
                    <div style={{ fontSize: 9, color: 'var(--t3)' }}>
                      Insufficient journal history — log more entries to see stability.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
